import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { Space, Button, Divider, Flex } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '@ant-design/pro-components'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AgGridReact } from 'ag-grid-react'
import { FileExcelOutlined } from '@ant-design/icons'
import { BaseExportParams } from 'ag-grid-enterprise'
import { thisYear, formatAsDate, now } from '../../../config/dayjsConfig'
import { DatePicker } from '../../../components/Form'
import { enumerateDaysBetweenDates } from '../../../utils/periodGroups/utils'
import {
  CustomExchangeRate,
  CustomExchangeRateSeries,
  CustomExchangeRateSeriesWithCompany,
  StatementType
} from '../types/exchangeRate'
import LoadingWrapper from '../../../components/Misc/LoadingWrapper'
import useCreateExchangeRate from '../queries/useCreateExchangeRate'
import useUpdateExchangeRate from '../queries/useUpdateExchangeRate'
import { useExchangeRates } from '..'
import ExchangeRateGrid from './ExchangeRateGrid'
import ExchangeRateAgChart from './ExchangeRateAgChart'
import useDefaultExchangeRates from '../queries/useDefaultExchangeRates'
import { useExchangeRateState } from '../hooks/useExchangeRateReducer'
import { CustomReportDataType } from '../../../redux/context/customReports/typesVariable'
import { showErrorMessage, showSuccessMessage } from '../../../utils/message'
import { useCtxCurrentFiscalYear } from '../../fiscalYear/queries/report/useCtxCurrentFiscalYear'
import useExchangeRateBlocker from '../hooks/useExchangeRateBlocker'
import sanitizeFilename from '../../../utils/sanitizeFilename'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

interface GridRowData {
  statementType: StatementType
  dataType: keyof typeof CustomReportDataType
  [key: string]: string | number | null // dynaamisia kuukausi-kenttiä varten
}

const ExchangeRateEdit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [resetKey, setResetKey] = useState(0)
  const { data: currentFiscalYear } = useCtxCurrentFiscalYear()
  const location = useLocation()
  const { record: { company } = {} } = (location.state ?? {}) as {
    record?: CustomExchangeRateSeriesWithCompany
  }

  // Käytä fiscal year:ia tai fallback kuluva vuosi
  const getInitialDateRange = useCallback((): [string | null, string | null] => {
    if (currentFiscalYear?.startDate && currentFiscalYear?.endDate) {
      const startDate = formatAsDate(currentFiscalYear.startDate)
      const endDate = formatAsDate(currentFiscalYear.endDate)
      if (startDate && endDate) {
        return [startDate, endDate]
      }
    }

    // Fallback: kuluva vuosi
    const currentYear = thisYear()
    return [formatAsDate(currentYear) || null, formatAsDate(currentYear.endOf('year')) || null]
  }, [currentFiscalYear])

  const [dateRange, setDateRange] = useState<[string | null, string | null]>(getInitialDateRange())
  const dates = useMemo(() => enumerateDaysBetweenDates(dateRange?.[0] || null, dateRange?.[1] || null), [dateRange])

  const { data: exchangeRates, isLoading: exchangeRatesLoading } = useExchangeRates()
  const { mutateAsync: updateExchangeRate, isPending: exchangeRateUpdatePending } = useUpdateExchangeRate()
  const { mutateAsync: createExchangeRate, isPending: exchangeRateCreatePending } = useCreateExchangeRate()

  const companyData = useMemo(() => exchangeRates?.find(r => r.companyId === company?.id), [exchangeRates])
  const { data: defaultRates } = useDefaultExchangeRates(companyData?.currency, companyData?.baseCurrency)

  // Reducer-pohjainen state management
  const { allRates, hasModifications, updateRate, reset } = useExchangeRateState(companyData?.rates || [])

  const gridRef = useRef<AgGridReact>(null)

  useExchangeRateBlocker(hasModifications)

  const exportParams = (): BaseExportParams => {
    return {
      fileName: sanitizeFilename(
        `${companyData?.company?.name}-${companyData?.currency}-${companyData?.baseCurrency}-${formatAsDate(now())}`,
        'export',
        'xlsx'
      ),
      processCellCallback: (params: {
        column: { getColId: () => string }
        value: any
        node?: { data?: GridRowData } | null
      }) => {
        const colId = params.column.getColId()
        if (colId.match(/^\d{2}\/\d{4}$/) && params.node?.data) {
          const value = params.value ?? defaultRates?.[`${params.node.data.statementType}-${colId}`]?.value
          if (value !== null && value !== undefined) {
            return parseFloat(Number(value).toFixed(4))
          }
          return ''
        }
        if (colId === 'dataType' || colId === 'statementType') {
          return t(`global:${params.value}`)
        }
        return params.value
      }
    }
  }

  const handleExcelExport = useCallback(() => {
    gridRef.current?.api.exportDataAsExcel(exportParams())
  }, [defaultRates, t])

  const defaultExportParams = useMemo(() => exportParams(), [companyData, defaultRates, t])

  useEffect(() => {
    reset()
    setResetKey(prev => prev + 1) // Muuta key → pakottaa grid:in uudelleenluonnin
  }, [companyData])

  useEffect(() => {
    setResetKey(prev => prev + 1) // Muuta key → pakottaa grid:in uudelleenluonnin
  }, [dates])

  const rowData = useMemo(() => {
    const rateIndex = new Map<string, CustomExchangeRate>()
    allRates.forEach(rate => {
      const key = `${rate.statementType}|${rate.dataType}|${rate.month}|${rate.year}`
      rateIndex.set(key, rate)
    })

    const categories = [
      { statementType: StatementType.pl, dataType: 'actuals' as const },
      { statementType: StatementType.pl, dataType: 'budget' as const },
      { statementType: StatementType.pl, dataType: 'forecast' as const },
      { statementType: StatementType.bs, dataType: 'actuals' as const },
      { statementType: StatementType.bs, dataType: 'budget' as const },
      { statementType: StatementType.bs, dataType: 'forecast' as const }
    ]

    return categories.map(category => {
      const row: GridRowData = {
        statementType: category.statementType,
        dataType: category.dataType
      }

      // O(1) lookups instead of O(M) findRate calls
      dates.forEach(date => {
        const monthKey = date.format('MM/YYYY')
        const lookupKey = `${category.statementType}|${CustomReportDataType[category.dataType]}|${
          date.month() + 1
        }|${date.year()}`
        const rate = rateIndex.get(lookupKey)
        row[monthKey] = rate?.value ?? null
      })

      return row
    })
  }, [allRates, dates])

  // Tallenna
  const onFinish = async () => {
    // rates pitäisi olla sellainen että sieltä on filtteröity ratet pois joilla on null arvo
    const filteredRates = allRates.filter(rate => rate.value !== null && rate.value !== undefined)

    const dataWithModifications = {
      ...companyData,
      rates: filteredRates
    } as CustomExchangeRateSeries

    try {
      if (dataWithModifications.id) {
        await updateExchangeRate({ data: dataWithModifications, id: dataWithModifications.id })
      } else {
        await createExchangeRate(dataWithModifications)
      }

      reset()
      showSuccessMessage()
    } catch (error) {
      showErrorMessage()
    }
  }

  if (!company) return null

  return (
    <LoadingWrapper loading={exchangeRatesLoading || exchangeRateUpdatePending || exchangeRateCreatePending}>
      <StyledContainer>
        <PageHeader
          title={`${company.name}`}
          subTitle={`${companyData?.currency}/${companyData?.baseCurrency}`}
          onBack={() => navigate(-1)}
          extra={
            <Flex align="center" gap={8} style={{ marginRight: -8 }}>
              <span>{t('global:time-period')}:</span>
              <DatePicker.RangePicker
                value={dateRange}
                picker="month"
                onChange={range => setDateRange([range?.[0] ?? null, range?.[1] ?? null])}
              />
              <Button icon={<FileExcelOutlined />} onClick={handleExcelExport}>
                Excel
              </Button>
              <Space.Compact>
                <Button onClick={onFinish} type="primary">
                  {t('global:save')}
                </Button>
              </Space.Compact>
            </Flex>
          }
        />

        <ExchangeRateGrid
          ref={gridRef}
          defaultRates={defaultRates}
          resetKey={resetKey}
          data={rowData}
          dates={dates}
          onChange={updateRate}
          defaultExportParams={defaultExportParams}
        />
        <Divider />

        <ExchangeRateAgChart defaultRates={defaultRates} allRates={allRates} dates={dates} />
      </StyledContainer>
    </LoadingWrapper>
  )
}

export default ExchangeRateEdit
